import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaceSmileIcon,
  PhotoIcon,
  PaperClipIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AttachmentLightbox, { type AttachmentKind } from './AttachmentLightbox';
import FileTypeIcon from './FileTypeIcon';
import { uploadFile } from '../../services/uploads';
import type { UploadResult } from '../../types/upload';

export interface ComposerSendPayload {
  body?: string;
  attachment_ids: string[];
}

interface MessageComposerProps {
  roomId: string | null;
  onSend: (payload: ComposerSendPayload) => void;
  disabled?: boolean;
}

interface PendingAttachment {
  key: string; // local key
  file: File;
  previewUrl: string; // local object URL for instant preview
  progress: number;
  uploaded?: UploadResult;
  error?: string;
}

function detectKind(mime: string, name: string): AttachmentKind {
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (mime === 'application/pdf' || /\.pdf$/i.test(name)) return 'pdf';
  if (mime.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(name)) return 'video';
  return 'other';
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface StagedAttachmentProps {
  item: PendingAttachment;
  onOpen: () => void;
  onRemove: () => void;
}

const StagedAttachment: React.FC<StagedAttachmentProps> = ({ item, onOpen, onRemove }) => {
  const kind = detectKind(item.file.type, item.file.name);
  const isUploading = !item.error && item.progress < 100;
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  if (kind === 'image') {
    return (
      <div className="relative group">
        <div
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen();
            }
          }}
          className="block w-20 h-20 rounded-lg overflow-hidden border border-border-secondary cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <img src={item.previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
        </div>
        {isUploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none rounded-lg">
            <span className="text-white text-xs font-medium">{item.progress}%</span>
          </div>
        )}
        {item.error && (
          <div className="absolute inset-0 bg-error-600/70 flex items-center justify-center pointer-events-none rounded-lg">
            <span className="text-white text-[10px] px-1 text-center">Failed</span>
          </div>
        )}
        <button
          type="button"
          onClick={handleRemove}
          aria-label="Remove attachment"
          className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-neutral-900 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="flex items-center gap-2 px-2.5 py-1.5 max-w-[260px] rounded-lg border border-border-secondary cursor-pointer hover:border-border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
    >
      <FileTypeIcon
        mime={item.file.type}
        filename={item.file.name}
        className="w-7 h-7 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="truncate text-xs font-medium text-text-primary">{item.file.name}</p>
        {item.error ? (
          <p className="text-[11px] text-error-600">{item.error}</p>
        ) : isUploading ? (
          <div className="mt-1 h-1 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-[width]"
              style={{ width: `${item.progress}%` }}
            />
          </div>
        ) : (
          <p className="text-[11px] text-text-tertiary">{humanSize(item.file.size)}</p>
        )}
      </div>
      <button
        type="button"
        aria-label="Remove attachment"
        onClick={handleRemove}
        className="shrink-0 p-1 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
      >
        <XMarkIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const MessageComposer: React.FC<MessageComposerProps> = ({ roomId, onSend, disabled }) => {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [viewer, setViewer] = useState<
    | { url: string; kind: AttachmentKind; filename: string; mime: string }
    | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = text.trim();
  const allUploaded = attachments.every((a) => a.uploaded);
  const canSend = !!roomId && !disabled && (trimmed.length > 0 || (attachments.length > 0 && allUploaded));

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, []);

  const startUpload = useCallback(
    async (file: File) => {
      if (!roomId) return;
      const localKey = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);
      setAttachments((prev) => [...prev, { key: localKey, file, previewUrl, progress: 0 }]);
      try {
        const result = await uploadFile(
          { file, resource_type: 'chat_attachment', resource_id: roomId },
          {
            onUploadProgress: (e) => {
              const total = e.total ?? file.size;
              const pct = total ? Math.round((e.loaded / total) * 100) : 0;
              setAttachments((prev) =>
                prev.map((a) => (a.key === localKey ? { ...a, progress: pct } : a))
              );
            },
          }
        );
        setAttachments((prev) =>
          prev.map((a) =>
            a.key === localKey ? { ...a, progress: 100, uploaded: result } : a
          )
        );
      } catch (err) {
        const msg = (err as Error)?.message ?? 'Upload failed';
        setAttachments((prev) =>
          prev.map((a) => (a.key === localKey ? { ...a, error: msg } : a))
        );
        toast.error(msg);
      }
    },
    [roomId]
  );

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(startUpload);
  };

  const removeAttachment = (key: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.key === key);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.key !== key);
    });
  };

  // Revoke any outstanding object URLs when the composer unmounts.
  useEffect(() => {
    return () => {
      setAttachments((prev) => {
        prev.forEach((a) => URL.revokeObjectURL(a.previewUrl));
        return prev;
      });
    };
  }, []);

  const handleSend = () => {
    if (!canSend) return;
    const ids = attachments
      .map((a) => a.uploaded?.id)
      .filter((id): id is string => !!id);
    onSend({ body: trimmed || undefined, attachment_ids: ids });
    attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    setText('');
    setAttachments([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="border-t border-border-secondary bg-surface-primary"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {attachments.length > 0 && (
        <div className="flex flex-wrap items-start gap-2 px-3 pt-2">
          {attachments.map((a) => (
            <StagedAttachment
              key={a.key}
              item={a}
              onOpen={() =>
                setViewer({
                  url: a.previewUrl,
                  kind: detectKind(a.file.type, a.file.name),
                  filename: a.file.name,
                  mime: a.file.type,
                })
              }
              onRemove={() => removeAttachment(a.key)}
            />
          ))}
        </div>
      )}

      <div className="flex items-end gap-1 px-3 py-2">
        <button
          type="button"
          aria-label="Insert emoji"
          disabled
          title="Coming soon"
          className="p-2 rounded-full text-text-tertiary opacity-60 cursor-not-allowed"
        >
          <FaceSmileIcon className="w-5 h-5" />
        </button>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            autoResize();
          }}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={disabled ? 'Select a chat to start typing…' : 'Type a message'}
          disabled={disabled || !roomId}
          className="flex-1 min-h-[36px] max-h-[160px] resize-none bg-transparent px-2 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none disabled:cursor-not-allowed"
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          aria-label="Attach image"
          onClick={() => imageInputRef.current?.click()}
          disabled={disabled || !roomId}
          className="p-2 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PhotoIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Attach file"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || !roomId}
          className="p-2 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PaperClipIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Send message"
          onClick={handleSend}
          disabled={!canSend}
          className="p-2 rounded-full bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-600 dark:hover:bg-primary-400"
        >
          <PaperAirplaneIcon className="w-5 h-5" />
        </button>
      </div>

      {viewer && (
        <AttachmentLightbox
          url={viewer.url}
          kind={viewer.kind}
          filename={viewer.filename}
          mime={viewer.mime}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
};

export default MessageComposer;
